import React, { useState } from 'react';
import { 
  Phone, Mail, MapPin, Clock, Send, 
  CheckCircle2, AlertCircle 
} from 'lucide-react';
import FadeIn from '../components/FadeIn';

const Contact = () => {
  const [status, setStatus] = useState(null); 

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    const formData = new FormData(e.target);
    
    // REPLACE THIS WITH YOUR ACTUAL KEY FROM WEB3FORMS
    formData.append("access_key", "ea9992a7-5209-47f7-9a82-bae459f618c3");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        e.target.reset();
      } else {
        console.log("Error", data);
        setStatus('error');
      }
    } catch (error) {
      console.log("Network Error", error);
      setStatus('error');
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-7xl mx-auto">
        
        <FadeIn direction="down">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-black text-[#1e3a8a] mb-4">Get in Touch</h1>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Ready to modernize your practice? Our team is available 24/7 to answer your questions.
            </p>
          </div>
        </FadeIn>

        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* LEFT SIDE: Contact Info */}
          <div className="space-y-6">
            <FadeIn direction="right" delay={0.1}>
              <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 flex items-start gap-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#14b8a6]/10 text-[#14b8a6] rounded-xl flex items-center justify-center shrink-0">
                  <Phone size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1e3a8a] mb-1">Phone Support</h3>
                  <p className="text-slate-500 text-sm mb-2">24/7 Dedicated Line</p>
                  <p className="text-lg font-bold text-[#1e3a8a]">+92 XXX XXXXXXX</p>
                </div>
              </div>
            </FadeIn>

            <FadeIn direction="right" delay={0.2}>
              <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 flex items-start gap-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#14b8a6]/10 text-[#14b8a6] rounded-xl flex items-center justify-center shrink-0">
                  <Mail size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1e3a8a] mb-1">Email Us</h3>
                  <p className="text-slate-500 text-sm mb-2">For audits & inquiries</p>
                  <p className="text-lg font-bold text-[#1e3a8a]">contact@optirevhealthcare.com</p>
                </div>
              </div>
            </FadeIn>

            <FadeIn direction="right" delay={0.3}>
              <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 flex items-start gap-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#14b8a6]/10 text-[#14b8a6] rounded-xl flex items-center justify-center shrink-0">
                  <MapPin size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1e3a8a] mb-1">USA Office</h3>
                  <p className="text-slate-500 text-sm mb-2">Corporate Headquarters</p>
                  <p className="text-lg font-bold text-[#1e3a8a]">
                    9436 W. Lake Mead Blvd<br />Ste 5 PMB 1034<br />Las Vegas, NV 89134
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* RIGHT SIDE: Contact Form */}
          <FadeIn direction="left" delay={0.2}>
            <div className="bg-white p-10 rounded-[2.5rem] shadow-xl border border-slate-100 h-full relative overflow-hidden">
               <div className="absolute top-0 right-0 w-64 h-64 bg-[#14b8a6]/5 rounded-full blur-3xl -z-0"></div>

              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6 relative z-10">Send a Message</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 ml-2">Your Name</label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-[#14b8a6] transition-all outline-none"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 ml-2">Email Address</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-[#14b8a6] transition-all outline-none"
                      placeholder="john@clinic.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-2">Subject</label>
                  <input 
                    type="text" 
                    name="subject"
                    required
                    className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-[#14b8a6] transition-all outline-none"
                    placeholder="Billing Inquiry"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-2">Message</label>
                  <textarea 
                    name="message"
                    required
                    rows="5"
                    className="w-full px-6 py-4 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-[#14b8a6] transition-all outline-none resize-none"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={status === 'sending'}
                  className="w-full bg-[#1e3a8a] text-white py-4 rounded-2xl font-bold text-lg hover:bg-[#14b8a6] transition-colors flex items-center justify-center gap-2 shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? (
                    <span className="animate-pulse">Sending...</span>
                  ) : (
                    <>Send Message <Send size={20} /></>
                  )}
                </button>

                {status === 'success' && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-xl">
                    <CheckCircle2 size={20} /> Message sent successfully!
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-xl">
                    <AlertCircle size={20} /> Error sending message.
                  </div>
                )}
              </form>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

export default Contact;